import { View, Text, FlatList, Pressable, Image, Platform, Alert } from 'react-native';
import { useState, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { Badge } from './Badge';
import { CategoryPickerModal } from './CategoryPickerModal';
import { Money } from './Money';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  buildCategorySections,
  filterCategorySections,
  type Category,
  type CategoryItem,
  type CategorySection,
} from './transaction-categories';
import { useConfirmationDialog } from './ConfirmationDialog';
import { webPrivacyProps } from '../lib/privacy';

export interface Transaction {
  id: string;
  name: string;
  date: string;
  amount: number;
  isPending: boolean;
  isReviewed: boolean;
  providerCategoryConfidenceLevel: string | null;
  categoryName: string | null;
  budgetCategoryId: number | null;
  deductible?: boolean;
  merchantName: string | null;
  merchantLogoUrl?: string | null;
  plaidCategoryIconUrl?: string | null;
  accountName?: string | null;
  goalId?: string | null;
}

/** Whether a transaction should show the low-confidence badge. */
function isLowConfidence(tx: Transaction): boolean {
  if (tx.isReviewed) return false;
  const level = tx.providerCategoryConfidenceLevel;
  return level === 'LOW' || level === 'UNKNOWN' || level === null;
}

interface Props {
  transactions: Transaction[];
  onUpdated?: () => unknown;
  emptyMessage?: string;
  /** Render a footer below the list (e.g. "Load more" button). */
  ListFooterComponent?: React.ReactElement | null;
}

/**
 * Renders a list of transactions where each row opens a bottom-sheet modal
 * to re-categorize the transaction. Self-contained: owns the modal state,
 * the categories query, and the update mutation.
 *
 * Used in both the main Transactions screen and onboarding step 3.
 */
export function TransactionList({
  transactions,
  onUpdated,
  emptyMessage = 'No transactions found.',
  ListFooterComponent = null,
}: Props) {
  const [recategorizingTx, setRecategorizingTx] = useState<Transaction | null>(null);

  const utils = trpc.useUtils();
  const updateMutation = trpc.transactions.update.useMutation();
  const reviewMutation = trpc.transactions.update.useMutation();
  const deductibleMutation = trpc.transactions.update.useMutation();
  const { confirm } = useConfirmationDialog();

  // Active life events keyed by budget category — when an unlinked
  // transaction's category is covered by exactly one event, offer a
  // one-tap link suggestion on the row.
  const lifeEventCoverageQ = trpc.budget.lifeEventCoverage.useQuery();
  const currentYearPrefix = String(new Date().getFullYear());
  const suggestionByCategoryId = new Map<
    number,
    { goalId: string; description: string }
  >();
  const ambiguousCategoryIds = new Set<number>();
  for (const event of lifeEventCoverageQ.data ?? []) {
    if (event.budgetCategoryId == null) continue;
    if (suggestionByCategoryId.has(event.budgetCategoryId)) {
      ambiguousCategoryIds.add(event.budgetCategoryId);
      continue;
    }
    suggestionByCategoryId.set(event.budgetCategoryId, {
      goalId: event.goalId,
      description: event.description,
    });
  }
  const suggestionFor = (tx: Transaction) => {
    if (tx.goalId || tx.budgetCategoryId == null) return null;
    if (Number(tx.amount) <= 0) return null;
    if (!tx.date.startsWith(currentYearPrefix)) return null;
    if (ambiguousCategoryIds.has(tx.budgetCategoryId)) return null;
    return suggestionByCategoryId.get(tx.budgetCategoryId) ?? null;
  };

  const linkToGoal = useCallback(
    async (txId: string, goalId: string | null) => {
      await updateMutation.mutateAsync({ id: txId, goalId });
      setRecategorizingTx(null);
      void utils.budget.lifeEventCoverage.invalidate();
      onUpdated?.();
    },
    [updateMutation, onUpdated, utils],
  );

  const doUpdate = useCallback(
    async (txId: string, budgetCategoryId: number | null, createMerchantRule?: boolean) => {
      await updateMutation.mutateAsync({ id: txId, budgetCategoryId, createMerchantRule });
      setRecategorizingTx(null);
      onUpdated?.();
    },
    [updateMutation, onUpdated],
  );

  const handleDismissConfidence = useCallback(
    async (txId: string) => {
      await reviewMutation.mutateAsync({ id: txId, isReviewed: true });
      onUpdated?.();
    },
    [reviewMutation, onUpdated],
  );

  const handleMarkNotDeductible = useCallback(
    async (txId: string) => {
      try {
        await deductibleMutation.mutateAsync({ id: txId, deductible: false });
        await onUpdated?.();
      } catch {
        Alert.alert('Update failed', 'Could not mark this transaction as not deductible.');
      }
    },
    [deductibleMutation, onUpdated],
  );

  const handleCategorySelected = useCallback(
    async (txId: string, budgetCategoryId: number | null) => {
      const tx = recategorizingTx;
      if (!tx) return;

      // If the transaction has a merchant and the category is changing, offer to create a rule
      if (tx.merchantName && budgetCategoryId !== tx.budgetCategoryId && budgetCategoryId !== null) {
        setRecategorizingTx(null);

        const choice = await confirm({
          title: 'Apply to all?',
          message: `Apply this category to all transactions from "${tx.merchantName}"?`,
          actions: [
            { key: 'cancel', label: 'Cancel', role: 'cancel' },
            { key: 'single', label: 'Just this one' },
            { key: 'all', label: `All from ${tx.merchantName}` },
          ],
        });

        if (choice === 'single') {
          await doUpdate(txId, budgetCategoryId);
        } else if (choice === 'all') {
          await doUpdate(txId, budgetCategoryId, true);
        }
      } else {
        await doUpdate(txId, budgetCategoryId);
      }
    },
    [confirm, recategorizingTx, doUpdate],
  );

  const logoUrl = (tx: Transaction): string | null =>
    tx.merchantLogoUrl ?? tx.plaidCategoryIconUrl ?? null;

  return (
    <>
      <FlatList
        data={transactions}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ gap: 0 }}
        ItemSeparatorComponent={() => <View className="h-px bg-gray-100 dark:bg-gray-700" />}
        renderItem={({ item: tx, index }) => {
          const icon = logoUrl(tx);
          const suggestion = suggestionFor(tx);
          return (
            <Pressable
              testID={`transaction-row-${index}`}
              accessibilityLabel={`Transaction ${index + 1}: ${tx.name}`}
              onPress={() => setRecategorizingTx(tx)}
              className="flex-row items-center justify-between py-3 px-4 bg-white dark:bg-gray-800 active:opacity-70"
            >
              {icon ? (
                <Image
                  source={{ uri: icon }}
                  style={{ width: 28, height: 28, borderRadius: 7, marginRight: 10 }}
                  resizeMode="contain"
                  accessibilityLabel={tx.merchantName ?? undefined}
                  {...(Platform.OS === 'web' && tx.merchantName ? { title: tx.merchantName } as any : {})}
                />
              ) : (
                <View style={{ width: 28, height: 28, marginRight: 10 }} />
              )}
              <View className="flex-1 pr-3">
                <Text
                  className="text-sm font-medium text-gray-800 dark:text-gray-200"
                  numberOfLines={1}
                  {...webPrivacyProps('private')}
                >
                  {tx.name}
                </Text>
                <View className="flex-row items-center gap-2 mt-0.5">
                  <Text className="text-xs text-gray-400 dark:text-gray-500">{formatDate(tx.date, 'short')}</Text>
                  {tx.categoryName ? (
                    <Badge label={tx.categoryName} variant="gray" />
                  ) : (
                    <Badge label="Uncategorized" variant="yellow" />
                  )}
                  {tx.isPending && <Badge label="Pending" variant="yellow" />}
                  {tx.goalId && <Badge label="Life event" variant="green" />}
                  {suggestion && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        void linkToGoal(tx.id, suggestion.goalId);
                      }}
                      disabled={updateMutation.isPending}
                      hitSlop={4}
                      accessibilityLabel={`Link to life event ${suggestion.description}`}
                    >
                      <Badge
                        label={`Link to "${suggestion.description}"?`}
                        variant="yellow"
                      />
                    </Pressable>
                  )}
                  {tx.deductible && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        void handleMarkNotDeductible(tx.id);
                      }}
                      disabled={deductibleMutation.isPending}
                      hitSlop={4}
                      accessibilityLabel="Mark not deductible"
                    >
                      <Badge label="Deductible" variant="green" />
                    </Pressable>
                  )}
                  {isLowConfidence(tx) && (
                    <Pressable
                      onPress={(e) => {
                        e.stopPropagation();
                        handleDismissConfidence(tx.id);
                      }}
                      hitSlop={4}
                    >
                      <Badge label="Low confidence" variant="yellow" />
                    </Pressable>
                  )}
                </View>
              </View>
              <Money
                weight={500}
                className={`text-sm ${
                  Number(tx.amount) < 0 ? 'text-forest-600 dark:text-green-500' : 'text-gray-800 dark:text-gray-200'
                }`}
              >
                {Number(tx.amount) < 0 ? '+' : ''}
                {formatCurrency(Math.abs(Number(tx.amount)))}
              </Money>
            </Pressable>
          );
        }}
        ListEmptyComponent={
          <View className="items-center py-8">
            <Text className="text-sm text-gray-400">{emptyMessage}</Text>
          </View>
        }
        ListFooterComponent={ListFooterComponent}
      />

      {/* Re-categorize bottom sheet */}
      <CategoryPickerModal
        visible={!!recategorizingTx}
        onClose={() => setRecategorizingTx(null)}
        selectedCategoryId={recategorizingTx?.budgetCategoryId ?? null}
        onSelect={(categoryId) =>
          recategorizingTx &&
          handleCategorySelected(recategorizingTx.id, categoryId)
        }
        title={recategorizingTx?.name ?? undefined}
        selectedGoalId={recategorizingTx?.goalId ?? null}
        onSelectGoal={(goalId) =>
          recategorizingTx && linkToGoal(recategorizingTx.id, goalId)
        }
      />
    </>
  );
}
